import i18n from '../../translations/i18n';

const ALLOWED_IMAGE_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);

const ALLOWED_TEXT_MIME_TYPES = new Set([
  'text/plain',
  'text/csv',
  'text/tab-separated-values',
  'text/markdown',
  'application/json',
]);

const TEXT_EXTENSIONS = new Set(['.txt', '.csv', '.tsv', '.md', '.json']);

const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024;
export const MAX_ATTACHMENTS_PER_MESSAGE = 3;

export type ArielAiChatAttachment = {
  filename: string;
  mimeType: string;
  dataBase64: string;
};

export type ArielAiChatAttachmentDisplay = {
  filename: string;
  mimeType: string;
  previewDataUrl?: string;
};

const getFileExtension = (filename: string): string => {
  const dotIndex = filename.lastIndexOf('.');
  return dotIndex >= 0 ? filename.slice(dotIndex).toLowerCase() : '';
};

const isImageMimeType = (mimeType: string): boolean => ALLOWED_IMAGE_MIME_TYPES.has(mimeType);

const isSupportedArielAiFile = (file: File): boolean => {
  if (ALLOWED_IMAGE_MIME_TYPES.has(file.type) || ALLOWED_TEXT_MIME_TYPES.has(file.type)) {
    return true;
  }
  const extension = getFileExtension(file.name);
  return TEXT_EXTENSIONS.has(extension);
};

const isDeferredArielAiFile = (file: File): boolean => {
  const extension = getFileExtension(file.name);
  return file.type === 'application/pdf' || extension === '.pdf' || extension === '.docx' || extension === '.doc';
};

const t = (key: string, options?: Record<string, unknown>) => i18n.t(`topicEditor:${key}`, options);

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error(t('arielAiAttachmentReadFailed', { filename: file.name })));
    reader.readAsDataURL(file);
  });

const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error(t('arielAiAttachmentReadFailed', { filename: file.name })));
    reader.readAsText(file);
  });

const encodeUtf8ToBase64 = (text: string): string => btoa(unescape(encodeURIComponent(text)));

const stripDataUrlPrefix = (dataUrl: string): string => {
  const commaIndex = dataUrl.indexOf(',');
  return commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
};

const inferTextMimeType = (file: File): string => {
  if (ALLOWED_TEXT_MIME_TYPES.has(file.type)) {
    return file.type;
  }
  const extension = getFileExtension(file.name);
  switch (extension) {
    case '.csv':
      return 'text/csv';
    case '.tsv':
      return 'text/tab-separated-values';
    case '.md':
      return 'text/markdown';
    case '.json':
      return 'application/json';
    default:
      return 'text/plain';
  }
};

export const validateArielAiAttachmentFile = (file: File): string | null => {
  if (isDeferredArielAiFile(file)) {
    return t('arielAiAttachmentDeferredUnsupported');
  }
  if (!isSupportedArielAiFile(file)) {
    return t('arielAiAttachmentUnsupportedType', { filename: file.name });
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return t('arielAiAttachmentFileTooLarge', { filename: file.name });
  }
  return null;
};

export const readFileAsAttachment = async (file: File): Promise<ArielAiChatAttachment> => {
  const validationError = validateArielAiAttachmentFile(file);
  if (validationError) {
    throw new Error(validationError);
  }

  if (isImageMimeType(file.type) || ALLOWED_IMAGE_MIME_TYPES.has(file.type)) {
    const dataUrl = await readFileAsDataUrl(file);
    return {
      filename: file.name,
      mimeType: file.type,
      dataBase64: stripDataUrlPrefix(dataUrl),
    };
  }

  const text = await readFileAsText(file);
  return {
    filename: file.name,
    mimeType: inferTextMimeType(file),
    dataBase64: encodeUtf8ToBase64(text),
  };
};

export const attachmentToDisplay = (attachment: ArielAiChatAttachment): ArielAiChatAttachmentDisplay => ({
  filename: attachment.filename,
  mimeType: attachment.mimeType,
  previewDataUrl: isImageMimeType(attachment.mimeType)
    ? `data:${attachment.mimeType};base64,${attachment.dataBase64}`
    : undefined,
});

export const ARIEL_AI_FILE_ACCEPT = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'text/plain',
  'text/csv',
  'text/tab-separated-values',
  'text/markdown',
  'application/json',
  '.txt',
  '.csv',
  '.tsv',
  '.md',
  '.json',
].join(',');
